import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import { useNavigate } from 'react-router-dom';

// ==================== TYPES (must be defined before cache code) ====================

export interface Organization {
  id: string;
  name: string;
  owner_user_id: string;
  max_members: number;
  created_at: string;
  updated_at: string;
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string | null;
  role: 'owner' | 'admin' | 'member';
  invited_by: string | null;
  invited_email: string | null;
  invited_at: string | null;
  accepted_at: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  profile?: {
    display_name: string | null;
  };
  email?: string;
}

export interface MemberPermission {
  id: string;
  member_id: string;
  module: string;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

export interface DataAssignment {
  id: string;
  member_id: string;
  table_name: string;
  record_id: string;
  assigned_at: string;
}

export const MODULES = [
  { key: 'transactions', label: 'Lançamentos' },
  { key: 'accounts', label: 'Contas Bancárias' },
  { key: 'chart_of_accounts', label: 'Plano de Contas' },
  { key: 'suppliers', label: 'Fornecedores' },
  { key: 'cost_centers', label: 'Centros de Custo' },
  { key: 'payment_methods', label: 'Formas de Pagamento' },
  { key: 'reports', label: 'Relatórios' },
  { key: 'settings', label: 'Configurações' },
] as const;

export type ModuleKey = typeof MODULES[number]['key'];

// ==================== CACHE HELPERS ====================
const ORG_CACHE_KEY_PREFIX = 'org_cache_v1:';
const SUPABASE_AUTH_KEY = 'sb-temtxtpbfjdxjzdoihwr-auth-token';

interface OrgCacheData {
  organization: Organization | null;
  members: OrganizationMember[];
  currentMember: OrganizationMember | null;
  cachedAt: number;
}

// Get userId synchronously from Supabase's local storage (before AuthContext loads)
function getStoredUserId(): string | null {
  try {
    const stored = localStorage.getItem(SUPABASE_AUTH_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed?.user?.id || null;
  } catch {
    return null;
  }
}

// Read cache fresh each time the hook initializes (lazy initialization)
// This ensures data always reflects the latest cached state
function getInitialCache(): OrgCacheData | null {
  const userId = getStoredUserId();
  if (!userId) return null;
  return readOrgCache(userId);
}

function readOrgCache(userId: string): OrgCacheData | null {
  try {
    const cached = localStorage.getItem(`${ORG_CACHE_KEY_PREFIX}${userId}`);
    if (!cached) return null;
    const data = JSON.parse(cached) as OrgCacheData;
    // Cache valid for 24 hours
    if (Date.now() - data.cachedAt > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(`${ORG_CACHE_KEY_PREFIX}${userId}`);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function writeOrgCache(userId: string, data: Omit<OrgCacheData, 'cachedAt'>) {
  try {
    // Remove sensitive data (invited_email) before caching
    const sanitizedMembers = data.members.map(m => ({
      ...m,
      invited_email: null, // Never cache invited_email for security
      email: undefined,
    }));
    const sanitizedCurrentMember = data.currentMember ? {
      ...data.currentMember,
      invited_email: null,
      email: undefined,
    } : null;
    
    localStorage.setItem(`${ORG_CACHE_KEY_PREFIX}${userId}`, JSON.stringify({
      organization: data.organization,
      members: sanitizedMembers,
      currentMember: sanitizedCurrentMember,
      cachedAt: Date.now(),
    }));
  } catch {
    // Ignore storage errors (quota, etc.)
  }
}

export function clearOrgCache(userId: string) {
  try {
    localStorage.removeItem(`${ORG_CACHE_KEY_PREFIX}${userId}`);
  } catch {
    // Ignore errors
  }
}

export function useOrganization() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Lazy initialization: read fresh cache from localStorage each time the hook mounts
  // This eliminates flickering when navigating between tabs
  const [organization, setOrganization] = useState<Organization | null>(
    () => getInitialCache()?.organization ?? null
  );
  const [members, setMembers] = useState<OrganizationMember[]>(
    () => getInitialCache()?.members ?? []
  );
  const [currentMember, setCurrentMember] = useState<OrganizationMember | null>(
    () => getInitialCache()?.currentMember ?? null
  );
  const [loading, setLoading] = useState(() => !getInitialCache());
  
  const [memberPermissions, setMemberPermissions] = useState<Record<string, MemberPermission[]>>({});
  const [dataAssignments, setDataAssignments] = useState<Record<string, DataAssignment[]>>({});

  const isOwner = currentMember?.role === 'owner';
  const isAdmin = currentMember?.role === 'admin';
  const canManageMembers = isOwner;

  const fetchOrganization = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    // DON'T set loading=true if we have cached data - prevents flickering
    // Only set loading=true on first load without cache

    try {
      // Get organization for current user
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .single();

      if (orgError) {
        logger.error('Error fetching organization');
        setLoading(false);
        return;
      }

      setOrganization(orgData);

      // Get members without sensitive invitation email data.
      // Type for the secure view result
      type SecureViewMember = {
        id: string;
        organization_id: string;
        user_id: string | null;
        role: 'owner' | 'admin' | 'member';
        invited_by: string | null;
        invited_at: string | null;
        accepted_at: string | null;
        created_at: string;
        updated_at: string;
      };

      const { data: membersData, error: membersError } = await supabase
        .from('organization_members_secure' as any)
        .select('id, organization_id, user_id, role, invited_by, invited_at, accepted_at, created_at, updated_at')
        .eq('organization_id', orgData.id) as { data: SecureViewMember[] | null; error: any };

      let enrichedMembers: OrganizationMember[] = [];
      let current: OrganizationMember | null = null;

      if (membersError) {
        logger.error('Error fetching members');
      } else {
        // Get profile data for each member
        const memberIds = (membersData || []).filter(m => m.user_id).map(m => m.user_id) as string[];
        
        let profilesMap: Record<string, { display_name: string | null }> = {};
        
        if (memberIds.length > 0) {
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('user_id, display_name')
            .in('user_id', memberIds);
          
          profilesMap = (profilesData || []).reduce((acc, p) => {
            acc[p.user_id] = { display_name: p.display_name };
            return acc;
          }, {} as Record<string, { display_name: string | null }>);
        }

        const currentMemberRow = (membersData || []).find(m => m.user_id === user.id);
        let invitationEmails: Record<string, string> = {};
        if (currentMemberRow?.role === 'owner' || currentMemberRow?.role === 'admin') {
          const { data: invitationData, error: invitationError } = await supabase.functions.invoke(
            'get-pending-invitations',
            { body: { organization_id: orgData.id } },
          );
          if (!invitationError && Array.isArray(invitationData?.members)) {
            invitationEmails = invitationData.members.reduce(
              (acc: Record<string, string>, member: { id: string; email: string | null }) => {
                if (member.email) acc[member.id] = member.email;
                return acc;
              },
              {},
            );
          }
        }

        enrichedMembers = (membersData || []).map(m => ({
          ...m,
          role: m.role as 'owner' | 'admin' | 'member',
          profile: m.user_id ? profilesMap[m.user_id] : undefined,
          invited_email: invitationEmails[m.id] || null,
          email: invitationEmails[m.id] || undefined,
        }));

        setMembers(enrichedMembers);
        
        // Find current member
        current = enrichedMembers.find(m => m.user_id === user.id) || null;
        setCurrentMember(current);
      }

      // Save to cache for instant hydration on next load
      writeOrgCache(user.id, {
        organization: orgData,
        members: enrichedMembers,
        currentMember: current,
      });

      // Get all permissions
      const { data: permsData } = await supabase
        .from('member_permissions')
        .select('*');

      if (permsData) {
        const permsMap = permsData.reduce((acc, p) => {
          if (!acc[p.member_id]) acc[p.member_id] = [];
          acc[p.member_id].push(p);
          return acc;
        }, {} as Record<string, MemberPermission[]>);
        setMemberPermissions(permsMap);
      }

      // Get data assignments
      const { data: assignData } = await supabase
        .from('data_assignments')
        .select('*');

      if (assignData) {
        const assignMap = assignData.reduce((acc, a) => {
          if (!acc[a.member_id]) acc[a.member_id] = [];
          acc[a.member_id].push(a);
          return acc;
        }, {} as Record<string, DataAssignment[]>);
        setDataAssignments(assignMap);
      }

    } catch (error) {
      logger.error('Error in fetchOrganization');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  // ==================== REALTIME SUBSCRIPTION ====================
  // Listen for DELETE events on organization_members to handle immediate logout
  // when the current user is removed from the organization
  useEffect(() => {
    if (!user || !organization) return;

    // Each hook instance needs its own channel. Reusing a subscribed channel name
    // makes supabase-js return the existing channel and reject additional callbacks.
    const channelName = `membership-removal:${organization.id}:${user.id}:${crypto.randomUUID()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'organization_members',
          filter: `organization_id=eq.${organization.id}`,
        },
        async (payload) => {
          const deletedRecord = payload.old as { id?: string; user_id?: string | null };
          
          // Check if the deleted member is the current user
          if (deletedRecord.user_id === user.id) {
            // Clear cache before logout
            clearOrgCache(user.id);
            
            // Show notification to user
            toast({
              variant: "destructive",
              title: "Acesso removido",
              description: "Você foi removido da organização. Saindo...",
            });
            
            // Sign out and redirect to auth
            await supabase.auth.signOut();
            navigate('/auth', { replace: true });
          } else {
            // Another member was removed - just refresh the list
            setMembers(prev => prev.filter(m => m.id !== deletedRecord.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, organization, navigate]);

  const inviteMember = async (email: string, role: 'admin' | 'member') => {
    if (!organization || !user || !canManageMembers) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Você não tem permissão para convidar membros.",
      });
      return false;
    }

    // Check member limit
    const activeMembers = members.filter(m => m.accepted_at || m.role === 'owner');
    if (activeMembers.length >= organization.max_members) {
      toast({
        variant: "destructive",
        title: "Limite atingido",
        description: `Você pode ter no máximo ${organization.max_members} usuários na sua organização.`,
      });
      return false;
    }

    try {
      const normalizedEmail = email.trim().toLowerCase();
      const { data, error: inviteError } = await supabase.functions.invoke('send-invite', {
        body: {
          email: normalizedEmail,
          organization_id: organization.id,
          role,
        }
      });

      if (inviteError || data?.error) {
        let responseError = data?.error || inviteError?.message;
        if (inviteError && 'context' in inviteError && inviteError.context instanceof Response) {
          const errorBody = await inviteError.context.clone().json().catch(() => null);
          if (errorBody && typeof errorBody.error === 'string') responseError = errorBody.error;
        }
        throw new Error(responseError || 'Falha ao enviar convite');
      }

      toast({
        title: "Convite enviado",
        description: `Um convite foi enviado para ${normalizedEmail}.`,
      });

      await fetchOrganization();
      return true;

    } catch (error: any) {
      logger.error('Error inviting member');
      toast({
        variant: "destructive",
        title: "Erro ao convidar",
        description: error.message || "Não foi possível enviar o convite.",
      });
      return false;
    }
  };

  const removeMember = async (memberId: string) => {
    if (!canManageMembers) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Você não tem permissão para remover membros.",
      });
      return false;
    }

    const member = members.find(m => m.id === memberId);
    if (member?.role === 'owner') {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não é possível remover o proprietário da organização.",
      });
      return false;
    }

    try {
      const { data, error } = await supabase.functions.invoke('remove-member', {
        body: { member_id: memberId },
      });

      if (error || (data as any)?.error) {
        throw new Error((data as any)?.error || error?.message || 'Não foi possível remover o membro.');
      }

      // Immediately update local state for instant feedback
      setMembers(prev => prev.filter(m => m.id !== memberId));

      // Clear cache to force fresh data on next load
      if (user) {
        clearOrgCache(user.id);
      }

      toast({
        title: "Membro removido",
        description: "O membro foi removido da organização.",
      });

      // Refresh in background to ensure consistency
      fetchOrganization();
      return true;

    } catch (error: any) {
      logger.error('Error removing member');
      toast({
        variant: "destructive",
        title: "Erro ao remover",
        description: error.message || 'Não foi possível remover o membro.',
      });
      return false;
    }
  };

  const updateMemberRole = async (memberId: string, newRole: 'admin' | 'member') => {
    if (!canManageMembers) return false;

    try {
      const { error } = await supabase
        .from('organization_members')
        .update({ role: newRole })
        .eq('id', memberId);

      if (error) throw error;

      toast({
        title: "Função atualizada",
        description: "A função do membro foi atualizada.",
      });

      await fetchOrganization();
      return true;

    } catch (error: any) {
      logger.error('Error updating role');
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message,
      });
      return false;
    }
  };

  const updateMemberPermissions = async (memberId: string, permissions: Partial<MemberPermission>[]) => {
    if (!canManageMembers) return false;

    try {
      // Delete existing permissions
      await supabase
        .from('member_permissions')
        .delete()
        .eq('member_id', memberId);

      // Insert new permissions
      if (permissions.length > 0) {
        const { error } = await supabase
          .from('member_permissions')
          .insert(permissions.map(p => ({
            member_id: memberId,
            module: p.module,
            can_view: p.can_view ?? false,
            can_create: p.can_create ?? false,
            can_edit: p.can_edit ?? false,
            can_delete: p.can_delete ?? false,
          })));

        if (error) throw error;
      }

      toast({
        title: "Permissões atualizadas",
        description: "As permissões do membro foram atualizadas.",
      });

      await fetchOrganization();
      return true;

    } catch (error: any) {
      console.error('Error updating permissions:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message,
      });
      return false;
    }
  };

  const updateDataAssignments = async (
    memberId: string, 
    tableName: string, 
    recordIds: string[]
  ) => {
    if (!canManageMembers) return false;

    try {
      // Delete existing assignments for this table
      await supabase
        .from('data_assignments')
        .delete()
        .eq('member_id', memberId)
        .eq('table_name', tableName);

      // Insert new assignments
      if (recordIds.length > 0) {
        const { error } = await supabase
          .from('data_assignments')
          .insert(recordIds.map(recordId => ({
            member_id: memberId,
            table_name: tableName,
            record_id: recordId,
          })));

        if (error) throw error;
      }

      toast({
        title: "Atribuições atualizadas",
        description: "Os acessos do membro foram atualizados.",
      });

      await fetchOrganization();
      return true;

    } catch (error: any) {
      console.error('Error updating assignments:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message,
      });
      return false;
    }
  };

  const updateOrganizationName = async (name: string) => {
    if (!organization || !isOwner) return false;

    try {
      const { error } = await supabase
        .from('organizations')
        .update({ name })
        .eq('id', organization.id);

      if (error) throw error;

      toast({
        title: "Nome atualizado",
        description: "O nome da organização foi atualizado.",
      });

      await fetchOrganization();
      return true;

    } catch (error: any) {
      console.error('Error updating organization name:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message,
      });
      return false;
    }
  };

  const updateMemberName = async (memberId: string, displayName: string) => {
    if (!canManageMembers) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Você não tem permissão para editar nomes.",
      });
      return false;
    }

    const member = members.find(m => m.id === memberId);
    if (!member?.user_id) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Usuário não encontrado ou convite pendente.",
      });
      return false;
    }

    // Optimistic update - update state immediately
    setMembers(prevMembers => 
      prevMembers.map(m => 
        m.id === memberId 
          ? { ...m, profile: { ...m.profile, display_name: displayName } }
          : m
      )
    );

    // Also update currentMember if it's the one being edited
    if (currentMember?.id === memberId) {
      setCurrentMember(prev => prev ? { ...prev, profile: { ...prev.profile, display_name: displayName } } : null);
    }

    try {
      // Use upsert to handle case where profile doesn't exist yet
      const { error } = await supabase
        .from('profiles')
        .upsert(
          { user_id: member.user_id, display_name: displayName },
          { onConflict: 'user_id' }
        );

      if (error) throw error;

      toast({
        title: "Nome atualizado",
        description: "O nome do usuário foi atualizado com sucesso.",
      });

      // Refresh to sync with database (confirms optimistic update)
      await fetchOrganization();
      return true;

    } catch (error: any) {
      console.error('Error updating member name:', error);
      // Revert optimistic update on error
      await fetchOrganization();
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message || "Não foi possível atualizar o nome.",
      });
      return false;
    }
  };

  return {
    organization,
    members,
    memberPermissions,
    dataAssignments,
    currentMember,
    loading,
    isOwner,
    isAdmin,
    canManageMembers,
    inviteMember,
    removeMember,
    updateMemberRole,
    updateMemberPermissions,
    updateDataAssignments,
    updateOrganizationName,
    updateMemberName,
    refresh: fetchOrganization,
  };
}
